/**
 * Scanner -- QR code scanner for Thread-tagged plots.
 *
 * Uses the BarcodeDetector Web API (Chrome, Safari, Edge) to read QR codes
 * from the device camera. Falls back to a user-friendly message on browsers
 * that lack support (Firefox).
 */

import { useRef, useEffect, useState, useCallback } from "react";
import { Box, Button, Callout, Text } from "@radix-ui/themes";
import { CheckIcon, WarningIcon } from "./icons";

/** Pattern to recognise Thread provenance URLs */
const THREAD_URL_PATTERN =
  /(?:\/api\/provenance\/a\/|thread\.flexcompute\.com\/a\/)([a-z0-9-]+)(?:[/?#]|$)/i;

export function extractThreadArtifactId(rawValue: string): string | null {
  return rawValue.match(THREAD_URL_PATTERN)?.[1] ?? null;
}

export interface ScanResult {
  artifactId: string;
  rawUrl: string;
}

interface ScannerProps {
  /** Called when a valid Thread QR code is detected */
  onDetected: (result: ScanResult) => void;
  /** Called when the scanner encounters a non-recoverable error */
  onError?: (error: string) => void;
}

type ScannerStatus =
  | "idle"
  | "requesting"
  | "scanning"
  | "detected"
  | "error"
  | "unsupported";

function hasBarcodeDetector(): boolean {
  return (
    typeof globalThis !== "undefined" &&
    "BarcodeDetector" in globalThis &&
    typeof (globalThis as any).BarcodeDetector === "function"
  );
}

export function Scanner({ onDetected, onError }: ScannerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<number>(0);
  const lastScanRef = useRef<number>(0);
  const detectedRef = useRef(false);

  const [status, setStatus] = useState<ScannerStatus>("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [nonThreadQR, setNonThreadQR] = useState(false);

  const cleanup = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    }
    if (streamRef.current) {
      for (const track of streamRef.current.getTracks()) {
        track.stop();
      }
      streamRef.current = null;
    }
  }, []);

  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  const startScanning = useCallback(async () => {
    if (!hasBarcodeDetector()) {
      setStatus("unsupported");
      return;
    }

    setStatus("requesting");
    setNonThreadQR(false);
    detectedRef.current = false;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: { ideal: 1280 } },
      });
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }

      setStatus("scanning");

      const detector = new (globalThis as any).BarcodeDetector({
        formats: ["qr_code"],
      });

      const scanFrame = async () => {
        if (detectedRef.current) return;

        const now = performance.now();
        // Throttle to ~10fps to save battery
        if (now - lastScanRef.current < 100) {
          rafRef.current = requestAnimationFrame(scanFrame);
          return;
        }
        lastScanRef.current = now;

        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (!video || !canvas || video.readyState < 2) {
          rafRef.current = requestAnimationFrame(scanFrame);
          return;
        }

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          rafRef.current = requestAnimationFrame(scanFrame);
          return;
        }
        ctx.drawImage(video, 0, 0);

        try {
          const barcodes = await detector.detect(canvas);
          if (barcodes.length > 0) {
            const rawValue: string = barcodes[0].rawValue;
            const artifactId = extractThreadArtifactId(rawValue);

            if (artifactId) {
              detectedRef.current = true;
              setStatus("detected");
              cleanup();
              onDetected({ artifactId, rawUrl: rawValue });
              return;
            } else {
              setNonThreadQR(true);
            }
          } else {
            setNonThreadQR(false);
          }
        } catch {
          // Detection can fail on individual frames -- continue scanning
        }

        rafRef.current = requestAnimationFrame(scanFrame);
      };

      rafRef.current = requestAnimationFrame(scanFrame);
    } catch (err: any) {
      cleanup();
      const msg =
        err?.name === "NotAllowedError"
          ? "Camera access was denied. Please allow camera access in your browser settings."
          : err?.name === "NotFoundError"
            ? "No camera found. Please connect a camera and try again."
            : `Camera error: ${err?.message ?? "unknown"}`;
      setErrorMsg(msg);
      setStatus("error");
      onError?.(msg);
    }
  }, [cleanup, onDetected, onError]);

  if (status === "unsupported") {
    return (
      <Callout.Root color="orange">
        <Callout.Icon>
          <WarningIcon />
        </Callout.Icon>
        <Callout.Text>
          Your browser does not support the QR scanner. Please use Chrome,
          Safari, or Edge, or enter the artifact URL manually.
        </Callout.Text>
      </Callout.Root>
    );
  }

  return (
    <Box style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Hidden canvas for frame capture */}
      <canvas ref={canvasRef} style={{ display: "none" }} />

      {/* Video feed */}
      <Box
        style={{
          position: "relative",
          borderRadius: 8,
          overflow: "hidden",
          background: "#000",
          minHeight: status === "scanning" ? undefined : 200,
        }}
      >
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={{
            width: "100%",
            display: status === "scanning" ? "block" : "none",
            borderRadius: 8,
          }}
        />

        {/* Scanning overlay */}
        {status === "scanning" && (
          <Box
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
            }}
          >
            <Box
              style={{
                width: "60%",
                maxWidth: 240,
                aspectRatio: "1",
                border: nonThreadQR
                  ? "3px solid var(--orange-9)"
                  : "3px solid var(--accent-9)",
                borderRadius: 12,
                animation: "pulse 2s ease-in-out infinite",
              }}
            />
          </Box>
        )}

        {/* Detected overlay */}
        {status === "detected" && (
          <Box
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(0, 180, 80, 0.25)",
            }}
          >
            <CheckIcon size={48} />
          </Box>
        )}
      </Box>

      {/* Status messages */}
      {status === "idle" && (
        <Button size="3" onClick={startScanning}>
          Start Camera
        </Button>
      )}

      {status === "requesting" && (
        <Text size="2" color="gray" align="center">
          Requesting camera access...
        </Text>
      )}

      {status === "scanning" && !nonThreadQR && (
        <Text size="2" color="gray" align="center">
          Point your camera at a Thread QR code
        </Text>
      )}

      {nonThreadQR && (
        <Callout.Root color="orange" size="1">
          <Callout.Icon>
            <WarningIcon />
          </Callout.Icon>
          <Callout.Text>
            QR code detected but it is not a Thread artifact. Keep scanning.
          </Callout.Text>
        </Callout.Root>
      )}

      {status === "error" && (
        <>
          <Callout.Root color="red" size="1">
            <Callout.Icon>
              <WarningIcon />
            </Callout.Icon>
            <Callout.Text>{errorMsg}</Callout.Text>
          </Callout.Root>
          <Button size="2" variant="outline" onClick={startScanning}>
            Try Again
          </Button>
        </>
      )}

      {status === "scanning" && (
        <Button
          size="2"
          variant="soft"
          color="gray"
          onClick={() => {
            cleanup();
            setStatus("idle");
          }}
        >
          Stop Camera
        </Button>
      )}

      {/* Pulse animation */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.6; transform: scale(0.96); }
          50% { opacity: 1; transform: scale(1.02); }
        }
      `}</style>
    </Box>
  );
}
